import moment from 'moment-mini'
import { of } from 'rxjs'
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import {
  Member,
  MEMBER_STATUSES_THAT_NEED_OPERATION_REDIRECT_CHECK,
  MemberStatus
} from 'src/app/features/member/member'
import { persistentUserRoleActions } from 'src/app/features/role/user-role/persistent-user-role/persistent-user-role.actions'
import { userRoleActions } from 'src/app/features/role/user-role/user-role.actions'
import { Answer } from 'src/app/features/survey/answer/answer'
import { answerActions } from 'src/app/features/survey/answer/answer.actions'
import { noOperationAction } from 'src/app/shared/resource-effects'
import { RegisterUploadProfilePictureService } from 'src/app/shared/services/register-upload-profile-picture.service'
import { RegisterUploadedDocumentsService } from 'src/app/shared/services/register-uploaded-documents.service'
import { ConfigurationService } from 'src/app/shared/services/configuration.service'
import { environment } from 'src/environments/environment'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { QueryClient } from '@tanstack/angular-query-experimental'
import { AppState } from '../core.state'
import { LocalStorageKeys } from '../local-storage.keys'
import { LocalStorageService } from '../local-storage.service'
import { NotificationService } from '../notification.service'
import { settingsActions } from '../settings/settings.actions'
import { authActions } from './auth.actions'
import { authSelectors } from './auth.selectors'
import { AuthService } from './auth.service'
import { TokenResponse } from './interfaces'
import { oidcActions } from './oidc/oidc.actions'
import { oidcSelectors } from './oidc/oidc.selectors'
import { operationActions } from 'src/app/features/operation/operation.actions'
import { AppRoutesNS } from '../../app-routes.namespace'
import { resolveMobileAppRedirect } from 'src/app/shared/utils/mobile-app-redirect'
import { isScanRedirectUrl } from 'src/app/shared/utils/scan-url'
import { cartItemActions } from 'src/app/features/cart/cart-item/cart-item.actions'
import { CartItem } from 'src/app/features/cart/cart-item/cart-item'
import { operationSelectors } from 'src/app/features/operation/operation.selectors'
import { OperationState } from 'src/app/features/operation/operation.reducer'
import {
  Operation,
  operationShouldRedirectToOperations
} from 'src/app/features/operation/operation'

const OIDC_REAUTH_ATTEMPTED_AT_KEY = 'delcom_oidc_reauth_attempted_at'
const OIDC_REAUTH_MIN_INTERVAL_SECONDS = 120

type ActionForTokenValidation =
  | ReturnType<typeof authActions.logout>
  | ReturnType<typeof authActions.authenticationSuccess>
  | ReturnType<typeof authActions.attemptOidcReauthentication>
  | ReturnType<typeof oidcActions.fetchTokenResponseByRefreshToken>
  | ReturnType<typeof authActions.attemptTokenRefresh>
  | ReturnType<typeof noOperationAction>

@Injectable()
export class AuthEffects implements OnInitEffects {
  private oidcReauthenticationRedirectStarted = false

  constructor(
    private actions$: Actions,
    private localStorageService: LocalStorageService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router,
    private store: Store<AppState>,
    private registerUploadDocumentService: RegisterUploadedDocumentsService,
    private registerUploadProfilePictureService: RegisterUploadProfilePictureService,
    private configurationService: ConfigurationService,
    private queryClient: QueryClient
  ) {}

  ngrxOnInitEffects() {
    this.store.dispatch(authActions.resetIsLoggedIn())
    return authActions.attemptAuthFromStorage()
  }

  login = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.login),
      switchMap((authLogin) =>
        this.authService.login(authLogin.email, authLogin.password).pipe(
          map((tokenResponse: TokenResponse) => {
            this.handlePostAuthSuccess()
            return authActions.authenticationSuccess({ tokenResponse })
          }),
          catchError((error) => of(authActions.loginFailure({ error })))
        )
      )
    )
  )

  clearTanStackQueryCacheForAuthBoundary = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.login, authActions.register, authActions.logout),
        tap(() => this.queryClient.clear())
      ),
    { dispatch: false }
  )

  oidcAuthenticationSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(oidcActions.loadTokenResponse),
      tap((action) => {
        console.log('Oidc login success', action)
        window.sessionStorage.removeItem(OIDC_REAUTH_ATTEMPTED_AT_KEY)
        this.store.dispatch(authActions.setIsOidc({ isOidc: true }))
      }),
      map((action) => {
        const tokenResponse: TokenResponse = action.tokenResponse
        this.handlePostAuthSuccess()
        this.store.dispatch(
          persistentUserRoleActions.setWasInitialized({ wasInitialized: true })
        )
        return authActions.authenticationSuccess({ tokenResponse })
      })
    )
  )

  attemptAuthFromStorage = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.attemptAuthFromStorage),
      filter(() => !this.isOnAuthCallbackPage()),
      withLatestFrom(
        this.store.select(authSelectors.selectTokenResponse),
        this.store.select(authSelectors.selectIsOidc),
        this.store.select(oidcSelectors.selectProviderSettings)
      ),
      filter(([_action, tokenResponse, isOidc, oidcProviderSettings]) =>
        Boolean(tokenResponse && tokenResponse.accessToken)
      ),
      map(([_action, tokenResponse, isOidc, oidcProviderSettings]) => {
        return this.validateAndRefreshToken(
          tokenResponse,
          isOidc,
          oidcProviderSettings,
          true
        )
      }),
      catchError((error) => {
        console.error('Error during attemptAuthFromStorage', error)
        return of(noOperationAction())
      })
    )
  )

  fetchMember = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.authenticationSuccess, authActions.fetchMember),
      switchMap((action) =>
        this.authService.getMember().pipe(
          map((member) =>
            authActions.loadMember({
              member,
              ...('surveyAnswers' in action &&
                action.surveyAnswers?.length && {
                  surveyAnswers: action.surveyAnswers
                })
            })
          ),
          catchError((error) => of(authActions.loginFailure({ error })))
        )
      )
    )
  )

  submitRegistrationSurveyAnswersOnMemberLoad = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadMember),
      filter((action) =>
        Boolean(action.member?.id && action.surveyAnswers?.length)
      ),
      map((action) =>
        answerActions.createMany({
          entities: action.surveyAnswers
            .filter((answer: Partial<Answer>) => Boolean(answer?.questionId))
            .map((answer: Partial<Answer>) => ({
              ...answer,
              memberId: action.member.id
            }))
        })
      )
    )
  )

  onLoadMember = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadMember),
      tap((action) => {
        const member = action.member
        this.handleMemberLoaded(member)
      }),
      filter(
        (action) =>
          this.isOnAuthPage() &&
          !this.shouldWaitForRequiredOperationRedirect(action.member)
      ),
      map(() => authActions.onLoadMemberRedirect())
    )
  )

  onLoadMemberRequiredOperationRedirect = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.loadMember),
        filter(
          (action) =>
            this.isOnAuthPage() &&
            this.shouldWaitForRequiredOperationRedirect(action.member)
        ),
        withLatestFrom(
          this.store.select(authSelectors.selectRedirectUrl),
          this.store.select(authSelectors.selectUrlPreLogin),
          this.configurationService.isMobileApp$
        ),
        tap(() => {
          this.store.dispatch(operationActions.fetchMyOperations({}))
        }),
        switchMap(([action, redirectUrl, preLoginUrl, isMobileApp]) =>
          this.actions$.pipe(
            ofType(operationActions.setInitialFetchDidFinish),
            take(1),
            withLatestFrom(this.store.select(operationSelectors.selectState)),
            tap(([, operationState]) => {
              if (
                this.hasRequiredCustomerOperation(action.member, operationState)
              ) {
                this.router.navigateByUrl(AppRoutesNS.Operations.OPERATIONS, {
                  replaceUrl: true
                })
                return
              }

              this.handleRedirectLogic(
                action.member,
                redirectUrl,
                preLoginUrl,
                isMobileApp
              )
            })
          )
        )
      ),
    { dispatch: false }
  )

  onLoadMemberRedirect = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.onLoadMemberRedirect),
        withLatestFrom(
          this.store.select(authSelectors.selectMember),
          this.store.select(authSelectors.selectRedirectUrl),
          this.store.select(authSelectors.selectUrlPreLogin),
          this.configurationService.isMobileApp$
        ),
        tap(([_action, member, redirectUrl, preLoginUrl, isMobileApp]) => {
          this.handleRedirectLogic(
            member,
            redirectUrl,
            preLoginUrl,
            isMobileApp
          )
        })
      ),
    { dispatch: false }
  )

  onUnauthorizedTokenRefresh = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.onUnauthorizedTokenRefresh),
      withLatestFrom(
        this.store.select(authSelectors.selectIsOidc),
        this.store.select(authSelectors.selectTokenResponse),
        this.store.select(authSelectors.selectIsLoggedIn)
      ),
      filter(() => {
        const currentUrl = this.router.url
        return Boolean(
          ![AppRoutesNS.Pages.LOGIN, AppRoutesNS.OIDC.AUTH_CALLBACK].find(
            (url) => currentUrl?.includes(url)
          )
        )
      }),
      map(([_action, isOidc, tokenResponse, isLoggedIn]) => {
        if (!tokenResponse) {
          return this.handleMissingTokenResponse(isLoggedIn)
        }
        return this.handleTokenRefresh(tokenResponse, isOidc)
      })
    )
  )

  attemptTokenRefresh = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.attemptTokenRefresh),
      exhaustMap((attemptTokenRefresh) =>
        this.authService.tokenRefresh(attemptTokenRefresh.refreshToken).pipe(
          map((tokenResponse: TokenResponse) =>
            authActions.authenticationSuccess({ tokenResponse })
          ),
          catchError((error) => {
            console.log(
              'Token refresh failed, resetting auth state using logout'
            )
            return of(authActions.logout({}))
          })
        )
      )
    )
  )

  attemptOidcReauthentication = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.attemptOidcReauthentication),
      filter(() => !this.oidcReauthenticationRedirectStarted),
      withLatestFrom(
        this.store.select(oidcSelectors.selectProviderSettings),
        this.store.select(oidcSelectors.selectProviderConfig)
      ),
      map(([_action, providerSettings, providerConfig]) => {
        if (
          !providerSettings ||
          !providerConfig ||
          this.isOnAuthPage() ||
          !this.markOidcReauthenticationAttempt()
        ) {
          console.log('Oidc re-authentication not possible, will logout')
          return authActions.logout({})
        }

        this.oidcReauthenticationRedirectStarted = true
        this.saveCurrentUrlForPostLoginRedirect()
        console.log('Will redirect to oidc provider for re-authentication')
        return oidcActions.startAuth()
      })
    )
  )

  updateProfile = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.updateProfile),
      switchMap((updateProfile) =>
        this.authService.updateProfile(updateProfile.params).pipe(
          map((member) => authActions.updateProfileSuccess({ member })),
          catchError((error) => {
            // TODO show registration error
            return of(authActions.updateProfileFailure({ error }))
          })
        )
      )
    )
  )
  onUpdateProfileSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.updateProfileSuccess),
      map((member) => authActions.loadMember(member))
    )
  )

  register = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.register),
      switchMap((action) =>
        this.authService.register(action.member).pipe(
          map((tokenResponse: TokenResponse) => {
            this.handlePostAuthSuccess()
            const storedPartialCartItems = window.localStorage.getItem(
              LocalStorageKeys.PARTIAL_CART_ITEMS
            )
            const partialCartItems: Partial<CartItem>[] = storedPartialCartItems
              ? JSON.parse(storedPartialCartItems)
              : []

            if (partialCartItems.length) {
              this.store.dispatch(
                cartItemActions.setCartItemUnauthenticatedToPartials({
                  cartItems: partialCartItems
                })
              )
            }
            this.store.dispatch(
              persistentUserRoleActions.setWasInitialized({
                wasInitialized: true
              })
            )
            return authActions.authenticationSuccess({
              tokenResponse,
              ...('surveyAnswers' in action &&
                action.surveyAnswers?.length && {
                  surveyAnswers: action.surveyAnswers
                })
            })
          }),
          catchError((error) => {
            console.error(error)
            return of(authActions.loginFailure({ error }))
          })
        )
      )
    )
  )

  logout = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.logout),
      tap((action) => {
        this.localStorageService.removeItem(LocalStorageKeys.PARTIAL_CART_ITEMS)
        this.localStorageService.removeItem(
          LocalStorageKeys.UN_AUTHENTICATED_CART_ITEMS
        )

        if (action.redirectTo != 'noRedirect') {
          if (this.isExternal(action.redirectTo)) {
            console.log(
              `Will redirect to external url in logout: ${action.redirectTo}`
            )

            window.location.href = action.redirectTo
          } else {
            if (!action.redirectTo) {
              this.preserveScanRedirectAfterLogout()
            }
            this.router.navigateByUrl(
              action.redirectTo ?? AppRoutesNS.Pages.LOGIN
            )
          }
        }

        this.localStorageService.clear([
          LocalStorageKeys.DISCOVERY_ACTIVITY_TAGS,
          LocalStorageKeys.DISCOVERY_RENT_TAGS,
          LocalStorageKeys.DISCOVERY_TIME_FILTERS,
          LocalStorageKeys.DISCOVERY_ACTIVITIES_LOCATION_IDS,
          LocalStorageKeys.WELCOME_SAW,
          LocalStorageKeys.COOKIE_CONSENT
        ])
      }),
      mergeMap((x) => [
        userRoleActions.resetState(),
        persistentUserRoleActions.setSelectedUserRole({
          userRole: null
        })
      ])
    )
  )
  logoutIfGuest = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.logoutIfGuest),
      withLatestFrom(this.store.select(authSelectors.selectMember)),
      filter(([action, member]) => member?.status == MemberStatus.GUEST),
      map(([action, member]) =>
        authActions.logout({
          redirectTo: action.redirectToShop ? '/shop' : 'noRedirect'
        })
      )
    )
  )
  resetPassword = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.resetPassword),
      switchMap((action) =>
        this.authService.changePassword(action.password, '', null, '').pipe(
          map((memberResponse: Member) => {
            this.store.dispatch(authActions.changePasswordSucces())
            return authActions.login({
              email: memberResponse.email,
              password: action.password
            })
          }),
          catchError((error) => of(authActions.resetPasswordFailure({ error })))
        )
      )
    )
  )

  sendPasswordResetEmail = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.sendPasswordResetEmail),
      switchMap((action) =>
        this.authService
          .sendPasswordResetEmail(action.email, action.resetPath)
          .pipe(
            map((result: boolean) =>
              authActions.sendPasswordResetEmailSuccess({ result: !result })
            ),
            catchError((error) =>
              of(authActions.sendPasswordResetEmailFailure({ error }))
            )
          )
      )
    )
  )

  changePassword = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.changePassword),
      switchMap((action) =>
        this.authService
          .changePassword(
            action.newPassword,
            action.currentPassword,
            action.userId,
            action.email
          )
          .pipe(
            map(() => authActions.changePasswordSucces()),
            catchError((error) =>
              of(authActions.changePasswordFailure({ error }))
            )
          )
      )
    )
  )

  changePasswordFailure = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.changePasswordFailure),
        tap((action) => {
          console.error(action.error)
          this.notificationService.emit('alerts.passwordChangedFail', 'error')
        })
      ),
    { dispatch: false }
  )

  anonymizeMember = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.anonymizeMember),
      switchMap((action) =>
        this.authService.anonymizeMember().pipe(
          map(() => authActions.logout({})),
          tap((action) => {
            this.notificationService.emit('alerts.success', 'success')
          }),
          catchError((error) =>
            of(authActions.anonymizeMemberFailure({ error }))
          )
        )
      )
    )
  )

  checkPassword = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.checkPassword),
      switchMap((authLogin) =>
        this.authService.login(authLogin.email, authLogin.password).pipe(
          map((tokenResponse: TokenResponse) =>
            authActions.checkPasswordSuccess({ tokenResponse })
          ),
          catchError((error) => of(authActions.loginFailure({ error })))
        )
      )
    )
  )

  private handlePostAuthSuccess(): void {
    this.store
      .select(authSelectors.selectRedirectUrl)
      .pipe(take(1))
      .subscribe((redirectUrl) => {
        if (!redirectUrl || redirectUrl?.trim().length == 0) {
          this.store.dispatch(
            authActions.saveUrlPreLogin({
              url: environment.moduleConfigs.authModule
                .afterLoginSuccessRedirect
            })
          )
        }
      })

    this.localStorageService.checkDelcomUnAuthenticatedCarItems()
  }

  private handleMemberLoaded(member: Member): void {
    this.registerUploadDocumentService.saveUploadedFile(member)
    this.registerUploadProfilePictureService.saveUploadedProfilePic()
    this.localStorageService.fetchCarItemIfThereIsUnAuthenticatedCarItems()

    const validLanguages = environment.availableLanguageCodes
    const language = validLanguages.includes(member.language)
      ? member.language
      : environment.defaultLanguage

    this.store.dispatch(
      settingsActions.setOneById({
        id: 'language',
        value: language
      })
    )
  }

  private validateAndRefreshToken(
    tokenResponse: TokenResponse,
    isOidc: boolean,
    oidcProviderSettings: any,
    isFromStorage: boolean
  ): ActionForTokenValidation {
    if (!tokenResponse?.accessToken?.length) {
      console.log('No auth authentication token, will call logout')
      return authActions.logout({})
    }

    const parsedAccessToken = this.authService.parseToken(
      tokenResponse?.accessToken
    )

    if (!parsedAccessToken) {
      return this.handleUnparseableToken(
        tokenResponse,
        oidcProviderSettings,
        isFromStorage
      )
    }

    const now = moment().unix()
    const tokenExpiry = parsedAccessToken.exp

    const fiveMinutes = 5 * 60
    const expiresIn = tokenExpiry - now

    if (expiresIn <= fiveMinutes) {
      console.log(`Token expires in ${expiresIn} seconds, attempting refresh`)
      return this.handleTokenRefresh(tokenResponse, isOidc)
    } else {
      console.log(
        `Token is valid for ${Math.floor(
          expiresIn / 60
        )} more minutes, and wont take action`
      )
      this.store.dispatch(authActions.fetchMember())
      return noOperationAction()
    }
  }

  private handleUnparseableToken(
    tokenResponse: TokenResponse,
    oidcProviderSettings: any,
    isFromStorage: boolean
  ): ActionForTokenValidation {
    // ITSME tokens are shit (can't be parsed)
    if (oidcProviderSettings?.name == 'AcmIdm' && tokenResponse?.accessToken) {
      console.log('AcmIdm token cant be parsed and will be assumed valid')

      if (isFromStorage) {
        this.handlePostAuthSuccess()
        this.store.dispatch(
          persistentUserRoleActions.setWasInitialized({ wasInitialized: true })
        )
      }
      return authActions.authenticationSuccess({ tokenResponse })
    } else {
      console.log('Parse authentication token exception, will call logout')
      return authActions.logout({})
    }
  }

  private handleTokenRefresh(
    tokenResponse: TokenResponse,
    isOidc: boolean
  ): ActionForTokenValidation {
    if (isOidc) {
      console.log('Will attempt oidc token refresh')

      if (!tokenResponse.refreshToken?.length) {
        console.log(
          'No oidc refresh token, will attempt silent re-authentication'
        )
        return authActions.attemptOidcReauthentication()
      }

      const parsedRefreshToken = this.authService.parseToken(
        tokenResponse.refreshToken
      )

      if (!parsedRefreshToken) {
        console.warn(
          'Failed to parse oidc refresh token, will just attempt refresh'
        )
        return oidcActions.fetchTokenResponseByRefreshToken({
          refreshToken: tokenResponse.refreshToken
        })
      } else if (parsedRefreshToken.exp < moment().unix()) {
        console.log(
          'OIDC refresh token expired, will attempt silent re-authentication'
        )
        return authActions.attemptOidcReauthentication()
      }

      return oidcActions.fetchTokenResponseByRefreshToken({
        refreshToken: tokenResponse.refreshToken
      })
    } else {
      console.log('Will attempt standard token refresh')
      return authActions.attemptTokenRefresh({
        refreshToken: tokenResponse.refreshToken
      })
    }
  }

  private handleMissingTokenResponse(
    isLoggedIn: boolean
  ): ActionForTokenValidation {
    console.log(
      'onUnauthorizedTokenRefresh got no tokenResponse, will perform logout'
    )

    if (environment.unauthenticatedShopping) {
      console.log(
        'onUnauthorizedTokenRefresh got no tokenResponse, but wont logout as unauthenticatedShopping is true'
      )
      this.store.dispatch(operationActions.setInitialFetchDidFinish())
      return noOperationAction()
    } else {
      if (isLoggedIn) {
        this.notificationService.emit(
          'alerts.authSessionFailure',
          'warning',
          3000
        )
      }
      return authActions.logout({})
    }
  }

  private isOnAuthPage(): boolean {
    const currentUrl = this.router.url
    const authPages = [
      AppRoutesNS.Pages.LOGIN,
      AppRoutesNS.Pages.REGISTER,
      AppRoutesNS.Pages.RESET_PASSWORD,
      AppRoutesNS.OIDC.AUTH_CALLBACK
    ]
    return authPages.some((page) => currentUrl?.includes(page))
  }

  private shouldRedirectToOnboarding(member: Member): boolean {
    const sawWelcome = this.localStorageService.getItem(
      LocalStorageKeys.WELCOME_SAW
    )
    let hasSeenWelcome = false
    if (sawWelcome) {
      hasSeenWelcome = JSON.parse(sawWelcome) === 'true'
    }

    return (
      environment.moduleConfigs.registrationModule.onboarding &&
      moment().diff(moment(member?.registrationDate), 'hours') < 36 &&
      !hasSeenWelcome
    )
  }

  private calculateRedirectUrl(
    redirectUrl: string,
    preLoginUrl: string
  ): string {
    if (preLoginUrl?.length > 3) {
      try {
        const url = new URL(preLoginUrl, window.location.origin)
        return url.pathname + url.search + url.hash
      } catch (error) {
        console.warn('Invalid preLoginUrl, using fallback:', preLoginUrl)
        return AppRoutesNS.Upcoming.UPCOMING_BOOKINGS
      }
    }

    if (redirectUrl?.length) {
      return redirectUrl
    }

    return AppRoutesNS.Upcoming.UPCOMING_BOOKINGS
  }

  private handleRedirectLogic(
    member: Member,
    redirectUrl: string,
    preLoginUrl: string,
    isMobileApp: boolean
  ): void {
    if (this.shouldRedirectToOnboarding(member)) {
      console.log(`First login, redirecting to ${AppRoutesNS.Welcome.WELCOME}`)
      this.router.navigateByUrl(AppRoutesNS.Welcome.WELCOME)
      return
    }

    let finalRedirect = this.calculateRedirectUrl(redirectUrl, preLoginUrl)

    if (this.isExternal(finalRedirect)) {
      console.warn('Blocked redirect to external URL:', finalRedirect)
      this.router.navigateByUrl(AppRoutesNS.Upcoming.UPCOMING_BOOKINGS)
      return
    }

    // Scan redirects must survive the mobile-app default so a scan
    // interrupted by login can complete after authentication
    if (isMobileApp && !isScanRedirectUrl(finalRedirect)) {
      finalRedirect = resolveMobileAppRedirect()
    }
    this.router.navigateByUrl(finalRedirect, { replaceUrl: true })
  }

  private isExternal(url: string): boolean {
    return /^http(?:s)?:\/{2}\S+$/.test(url)
  }

  private isOnAuthCallbackPage(): boolean {
    return window.location.pathname.includes(AppRoutesNS.OIDC.AUTH_CALLBACK)
  }

  private secondsSinceLastOidcReauthenticationAttempt(): number {
    const lastAttempt = Number(
      window.sessionStorage.getItem(OIDC_REAUTH_ATTEMPTED_AT_KEY)
    )

    if (!lastAttempt) {
      return Number.POSITIVE_INFINITY
    }

    return moment().unix() - lastAttempt
  }

  private markOidcReauthenticationAttempt(): boolean {
    if (
      this.secondsSinceLastOidcReauthenticationAttempt() <
      OIDC_REAUTH_MIN_INTERVAL_SECONDS
    ) {
      return false
    }

    window.sessionStorage.setItem(
      OIDC_REAUTH_ATTEMPTED_AT_KEY,
      String(moment().unix())
    )
    return true
  }

  private saveCurrentUrlForPostLoginRedirect(): void {
    const currentUrl = window.location.pathname + window.location.search

    this.store.dispatch(authActions.setRedirectUrl({ redirectUrl: currentUrl }))
    this.store.dispatch(authActions.saveUrlPreLogin({ url: currentUrl }))
  }

  // A failed token refresh during a scan can trigger logout before the scan
  // component even mounts, and the logout reducer wipes any saved redirect.
  // Re-save the scan URL (effects run after reducers) so logging back in
  // resumes the scan. Uses window.location because the router may not have
  // completed the initial navigation yet on a cold start.
  private preserveScanRedirectAfterLogout(): void {
    const currentUrl = window.location.pathname + window.location.search

    if (!isScanRedirectUrl(currentUrl)) {
      return
    }

    this.store.dispatch(authActions.setRedirectUrl({ redirectUrl: currentUrl }))
    this.store.dispatch(authActions.saveUrlPreLogin({ url: currentUrl }))
  }

  private shouldWaitForRequiredOperationRedirect(member: Member): boolean {
    return MEMBER_STATUSES_THAT_NEED_OPERATION_REDIRECT_CHECK.has(
      member?.status
    )
  }

  private hasRequiredCustomerOperation(
    member: Member,
    operationState: OperationState
  ): boolean {
    const operations: Operation[] = Object.values(operationState.entities ?? {})

    return operations.some(
      (operation) =>
        operation.memberId === member?.id &&
        operation.isPending &&
        operationShouldRedirectToOperations(operation)
    )
  }
}
